import {Injectable} from '@angular/core';
import {Store} from '@ngrx/store';
import {Observable, of, timer} from 'rxjs';
import {map, take} from 'rxjs/operators';
import type {MessengerSignalsPort} from './signals.port';
import {TypingMember} from './messenger.models';
import {FIXTURE_TYPING_NAME} from './messenger-fixture-data';
import {CollaborationState} from '../store/collaboration.reducer';
import {getCurrentChatConversation} from '../store/collaboration.selector';

/**
 * The signals port on the carbon mockup's data, for a dev build (ITP-E-194-02): read from the open conversation in the
 * store, and the mockup's member typing for a few seconds of every twenty.
 */
@Injectable({providedIn: 'root'})
export class MessengerSignalsFixture implements MessengerSignalsPort {
  constructor(private collaborationStore: Store<CollaborationState>) {
  }

  /** The first of the unread messages at the end of the open conversation. */
  firstUnreadSequence(conversationId: string): Observable<number | null> {
    return this.open(conversationId).pipe(map(conversation => {
      const messages = conversation?.messages || [];
      const unread = conversation?.unreadMessageCount || 0;
      return unread > 0 && messages.length >= unread ? messages[messages.length - unread].sequence ?? null : null;
    }));
  }

  /** Every message but the newest is delivered. */
  deliveredUpTo(conversationId: string): Observable<number | null> {
    return this.open(conversationId).pipe(map(conversation => {
      const messages = conversation?.messages || [];
      return messages.length > 1 ? messages[messages.length - 2].sequence ?? null : null;
    }));
  }

  markDelivered(conversationId: string, sequence: number): Observable<void> {
    return of(undefined);
  }

  typing(conversationId: string): Observable<TypingMember[]> {
    return timer(0, 4000).pipe(map(tick => tick % 5 === 1 ? [{email: 'marco.lindqvist@example.com', name: FIXTURE_TYPING_NAME}] : []));
  }

  setTyping(conversationId: string): Observable<void> {
    return of(undefined);
  }

  private open(conversationId: string): Observable<any> {
    return this.collaborationStore.select(getCurrentChatConversation).pipe(take(1),
      map(conversation => conversation?.id === conversationId ? conversation : null));
  }
}
